import { useRef, useState } from "react";
import { BarChart3, Paperclip, Send } from "lucide-react";
import { uploadsUrl, type ChatMessage } from "../lib/api";
import { Button, Input } from "./ui";

type VoteMeta = {
  question: string;
  options: { id: string; label: string }[];
};

export function ChatPanel({
  messages,
  onSend,
  onCreateVote,
  onCastVote,
  token,
  meetingId,
  appendMessage,
  guestUpload,
}: {
  messages: ChatMessage[];
  onSend: (text: string) => void;
  onCreateVote: (question: string, options: string[]) => void;
  onCastVote: (messageId: string, optionId: string) => void;
  token?: string | null;
  meetingId: string;
  appendMessage?: (msg: ChatMessage) => void;
  guestUpload?: { inviteToken: string; guestName: string };
}) {
  const [text, setText] = useState("");
  const [showVote, setShowVote] = useState(false);
  const [voteQuestion, setVoteQuestion] = useState("");
  const [voteOptions, setVoteOptions] = useState(["", ""]);
  const fileRef = useRef<HTMLInputElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  function senderName(msg: ChatMessage) {
    return msg.senderUser?.name || msg.guestName || "Guest";
  }

  return (
    <aside className="flex h-full flex-col glass-panel rounded-2xl">
      <header className="border-b border-white/10 px-4 py-3">
        <h2 className="font-display text-sm font-semibold tracking-tight">Meeting chat</h2>
        <p className="text-xs text-ink-500">Messages, files, and live polls</p>
      </header>

      <div className="flex-1 space-y-3 overflow-y-auto px-3 py-3">
        {messages.map((msg) => (
          <div key={msg.id} className="rounded-xl bg-white/5 px-3 py-2 ring-1 ring-white/5">
            <div className="mb-1 flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-accent-400">{senderName(msg)}</span>
              <span className="text-[10px] text-ink-500">
                {new Date(msg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            </div>

            {msg.type === "TEXT" && <p className="text-sm text-ink-100">{msg.content}</p>}

            {msg.type === "FILE" &&
              msg.attachments?.map((a) => (
                <a
                  key={a.id}
                  href={uploadsUrl(a.urlPath)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-accent-400 underline underline-offset-2"
                >
                  {a.filename}
                </a>
              ))}

            {msg.type === "VOTE" && (
              <VoteBlock
                msg={msg}
                onCastVote={onCastVote}
              />
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {showVote ? (
        <div className="space-y-2 border-t border-white/10 p-3">
          <Input
            placeholder="Poll question"
            value={voteQuestion}
            onChange={(e) => setVoteQuestion(e.target.value)}
          />
          {voteOptions.map((opt, i) => (
            <Input
              key={i}
              placeholder={`Option ${i + 1}`}
              value={opt}
              onChange={(e) => {
                const next = [...voteOptions];
                next[i] = e.target.value;
                setVoteOptions(next);
              }}
            />
          ))}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              className="flex-1"
              onClick={() => setVoteOptions((o) => [...o, ""])}
            >
              Add option
            </Button>
            <Button
              type="button"
              className="flex-1"
              onClick={() => {
                onCreateVote(voteQuestion, voteOptions);
                setShowVote(false);
                setVoteQuestion("");
                setVoteOptions(["", ""]);
              }}
            >
              Start poll
            </Button>
          </div>
        </div>
      ) : (
        <div className="border-t border-white/10 p-3">
          <div className="flex gap-2">
            <Input
              placeholder="Write a message…"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && text.trim()) {
                  onSend(text.trim());
                  setText("");
                }
              }}
            />
            <Button
              type="button"
              variant="ghost"
              aria-label="Attach file"
              onClick={() => fileRef.current?.click()}
              disabled={!token && !guestUpload}
            >
              <Paperclip className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              aria-label="Create poll"
              onClick={() => setShowVote(true)}
            >
              <BarChart3 className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              aria-label="Send"
              onClick={() => {
                if (!text.trim()) return;
                onSend(text.trim());
                setText("");
              }}
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const { api } = await import("../lib/api");
              try {
                if (token) {
                  const { message } = await api.uploadFile(token, meetingId, file);
                  appendMessage?.(message);
                } else if (guestUpload) {
                  const { message } = await api.uploadFileGuest(
                    meetingId,
                    file,
                    guestUpload.inviteToken,
                    guestUpload.guestName,
                  );
                  appendMessage?.(message);
                }
              } catch {
                /* handled by api */
              }
              e.target.value = "";
            }}
          />
        </div>
      )}
    </aside>
  );
}

function VoteBlock({
  msg,
  onCastVote,
}: {
  msg: ChatMessage;
  onCastVote: (messageId: string, optionId: string) => void;
}) {
  const meta = msg.metadata as VoteMeta | undefined;
  if (!meta?.options) return <p className="text-sm">{msg.content}</p>;

  const counts = meta.options.map((opt) => ({
    ...opt,
    count: msg.voteResponses?.filter((r) => r.optionId === opt.id).length ?? 0,
  }));
  const total = counts.reduce((s, c) => s + c.count, 0) || 1;

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{meta.question}</p>
      {counts.map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onCastVote(msg.id, opt.id)}
          className="block w-full rounded-lg bg-surface-800/80 px-2 py-1.5 text-left text-xs hover:bg-surface-700/80"
        >
          <div className="flex justify-between gap-2">
            <span>{opt.label}</span>
            <span className="text-ink-500">{opt.count}</span>
          </div>
          <div className="mt-1 h-1 overflow-hidden rounded bg-white/10">
            <div
              className="h-full bg-accent-500 transition-all"
              style={{ width: `${(opt.count / total) * 100}%` }}
            />
          </div>
        </button>
      ))}
    </div>
  );
}
